import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, BRAND_COLOR, baseChartOptions } from '../../analytics/shared/echarts-config';
import { ChartContainer } from '../../analytics/shared/ChartContainer';
import { EmptyState } from '../../analytics/shared/EmptyState';
import type { EngagementMetricsTrendPoint } from '../../../types/engagement-metrics.types';
import { buildEngagementMetricsTrendTooltip } from './engagement-metrics-trend-tooltip.util';

type EngagementMetricsTrendChartProps = {
  data: EngagementMetricsTrendPoint[];
};

const buildChartOptions = (data: EngagementMetricsTrendPoint[]) => ({
  ...baseChartOptions,
  tooltip: {
    trigger: 'axis' as const,
    formatter: (params: Array<{ dataIndex: number }>) => {
      const idx = params[0]?.dataIndex;
      if (idx == null) return '';
      const point = data[idx];
      if (!point) return '';

      const d = new Date(point.date);
      const dateStr = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });

      return buildEngagementMetricsTrendTooltip(dateStr, point.count, point.byType ?? {});
    },
  },
  xAxis: {
    type: 'category' as const,
    data: data.map((p) => p.date),
    axisLabel: {
      formatter: (value: string) => {
        const d = new Date(value);
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      },
      fontSize: 12,
      color: '#6b7280',
    },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: [
    {
      name: 'Engagements',
      type: 'line',
      data: data.map((p) => p.count),
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: BRAND_COLOR },
      itemStyle: { color: BRAND_COLOR },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(61, 101, 96, 0.25)' },
            { offset: 1, color: 'rgba(61, 101, 96, 0.02)' },
          ],
        },
      },
    },
  ],
});

export const EngagementMetricsTrendChart = ({ data }: EngagementMetricsTrendChartProps) => {
  if (data.length === 0) {
    return (
      <ChartContainer
        title="Engagement Trend"
        subtitle="Daily engagement volume over the selected period"
      >
        <EmptyState message="No engagement trend data available for the selected date range." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      title="Engagement Trend"
      subtitle="Daily engagement volume over the selected period"
    >
      <ReactEChartsCore
        echarts={echarts}
        option={buildChartOptions(data)}
        style={{ height: 300 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
